import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useCercaUsuaris } from '@/api/activacions';
import { useMissatgeError } from '@/api/errors';
import {
  useAltaUsuariInformar,
  useBaixaUsuariInformar,
  useUsuarisInformar,
  type OrigenUsuariInformar,
  type UsuariInformarDto,
} from '@/api/administracio';
import { useEditaUsuariInformar } from '@/api/ajustosEstructura';
import type { Canal } from '@/api/types';
import { Icona } from '@/components/Icona';
import { ErrorCarrega } from '@/pages/EstatsPagina';
import { Buit, Camp } from '@/pages/operacio/comu';
import { Permis } from '@/permisos/Permis';
import { useMe } from '@/permisos/MeContext';
import { Calaix, Confirmacio } from '../marc/Calaix';
import { CanalsChips } from './comu';
import { normalitza } from './suport';
import '../estructura.css';

/** Marca d'ordre de bytes perquè Excel obri el CSV en UTF-8. */
const BOM = String.fromCharCode(0xfeff);
const CORREU = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const BADGE_ORIGEN: Record<OrigenUsuariInformar, string> = {
  EXCEL: 'badge--ok',
  ENTRA: 'badge--avis',
  MANUAL: 'badge--gray',
};

function exportaCsv(
  nom: string,
  files: UsuariInformarDto[],
  cap: string[],
  canal: (c: Canal) => string,
) {
  const esc = (v: string | null | undefined) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const linies = [
    cap.map(esc).join(';'),
    ...files.map((u) =>
      [u.nom, u.email, u.telefon, u.canals.map(canal).join(', '), u.origen].map(esc).join(';'),
    ),
  ];
  const url = URL.createObjectURL(
    new Blob([BOM, linies.join('\n')], { type: 'text/csv;charset=utf-8' }),
  );
  const a = document.createElement('a');
  a.href = url;
  a.download = `${nom}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Alta o edició d'una persona a informar, en un calaix lateral. */
function FormPersona({
  serveiId,
  persona,
  onTanca,
}: {
  serveiId: number;
  persona: UsuariInformarDto | null;
  onTanca: () => void;
}) {
  const { t } = useTranslation('administracio');
  const missatgeError = useMissatgeError();
  const alta = useAltaUsuariInformar(serveiId);
  const edita = useEditaUsuariInformar(serveiId);
  const mutacio = persona ? edita : alta;
  const [u, setU] = useState<UsuariInformarDto>(
    persona ?? {
      serveiId,
      nom: '',
      email: '',
      telefon: '',
      origen: 'MANUAL',
      canals: ['PUSH', 'EMAIL'],
      actiu: true,
    },
  );
  const [cercaUsuari, setCercaUsuari] = useState('');
  const usuaris = useCercaUsuaris(persona ? '' : cercaUsuari);
  const existents = useUsuarisInformar(serveiId).data ?? [];
  const candidats = (usuaris.data ?? []).filter(
    (c) => !existents.some((e) => e.usuariId === c.id),
  );
  const nomesCanals = persona != null && persona.usuariId != null;
  const correuValid = !u.email || CORREU.test(u.email);
  const valid = persona
    ? u.nom.trim() !== '' && correuValid && u.canals.length > 0
    : u.usuariId != null && u.canals.length > 0;
  return (
    <Calaix
      etiqueta={t('estructura.informar.etiqueta')}
      titol={
        persona
          ? t('estructura.informar.editaTitol', { nom: persona.nom })
          : t('estructura.informar.nouTitol')
      }
      onTanca={onTanca}
      peu={
        <>
          <button type="button" className="btn btn--s" onClick={onTanca}>
            {t('estructura.crea.cancella')}
          </button>
          <button
            type="button"
            className="btn btn--dark btn--s"
            disabled={!valid || mutacio.isPending}
            onClick={() => mutacio.mutate(u, { onSuccess: onTanca })}
          >
            <Icona nom="checkmark" />
            {persona ? t('estructura.informar.desa') : t('estructura.informar.crea')}
          </button>
        </>
      }
    >
      {persona == null ? (
        <div className="camp">
          <span className="camp__label">{t('estructura.informar.usuari')}</span>
          {u.usuariId != null ? (
            <div className="list-row" data-usuari-triat={u.usuariId}>
              <div className="list-row__text">
                <div className="list-row__label strong">{u.nom}</div>
                <div className="list-row__sub">{u.email ?? ''}</div>
              </div>
              <button
                type="button"
                className="btn btn--xs"
                onClick={() => setU({ ...u, usuariId: null, nom: '', email: '' })}
              >
                {t('estructura.informar.canviaUsuari')}
              </button>
            </div>
          ) : (
            <>
              <label className="search">
                <Icona nom="magnifier" />
                <input
                  type="search"
                  value={cercaUsuari}
                  onChange={(e) => setCercaUsuari(e.target.value)}
                  placeholder={t('estructura.informar.cercaUsuari')}
                  aria-label={t('estructura.informar.cercaUsuari')}
                />
              </label>
              {cercaUsuari.trim().length >= 2 ? (
                candidats.length > 0 ? (
                  <div className="panel panel--pla">
                    {candidats.map((c) => {
                      const nom = [c.nom, c.cognoms].filter(Boolean).join(' ');
                      return (
                        <button
                          key={c.id}
                          type="button"
                          className="list-row"
                          style={{ width: '100%', textAlign: 'left' }}
                          onClick={() =>
                            setU({ ...u, usuariId: c.id, nom, email: c.email ?? '' })
                          }
                        >
                          <div className="list-row__text">
                            <div className="list-row__label strong">{nom}</div>
                            <div className="list-row__sub">
                              {[c.email, c.departament].filter(Boolean).join(' · ')}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : usuaris.isFetching ? null : (
                  <span className="camp__ajuda">{t('estructura.informar.senseUsuaris')}</span>
                )
              ) : (
                <span className="camp__ajuda">{t('estructura.informar.cercaUsuariAjuda')}</span>
              )}
            </>
          )}
        </div>
      ) : (
        <>
          {nomesCanals ? (
            <div className="info-banda">
              <Icona nom="info-circle" />
              {t('estructura.informar.origenUsuari')}
            </div>
          ) : null}
          <Camp etiqueta={t('estructura.informar.nom')}>
            <input
              className="input"
              value={u.nom}
              disabled={nomesCanals}
              onChange={(e) => setU({ ...u, nom: e.target.value })}
            />
          </Camp>
          <Camp etiqueta={t('estructura.informar.email')}>
            <input
              className="input"
              type="email"
              value={u.email ?? ''}
              disabled={nomesCanals}
              aria-invalid={!correuValid}
              onChange={(e) => setU({ ...u, email: e.target.value })}
            />
          </Camp>
          {!correuValid ? (
            <p className="error-text">{t('estructura.informar.correuInvalid')}</p>
          ) : null}
        </>
      )}
      <div className="camp">
        <span className="camp__label">{t('estructura.informar.canals')}</span>
        <CanalsChips
          etiqueta={t('estructura.informar.canals')}
          valor={u.canals}
          onCanvia={(canals) => setU({ ...u, canals })}
        />
        {u.canals.length === 0 ? (
          <span className="camp__ajuda">{t('estructura.informar.canalsBuit')}</span>
        ) : null}
      </div>
      {mutacio.isError ? <p className="error-text">{missatgeError(mutacio.error)}</p> : null}
    </Calaix>
  );
}

/**
 * Persones a informar d'un servei: llista amb cerca, alta i edició en calaix, baixa amb
 * confirmació, importació des d'Excel i exportació a CSV. Es fa servir a la pestanya
 * «Persones» del servei (Estructura) i a la pantalla global d'usuaris a informar.
 */
export function LlistaPersonesInformar({ serveiId }: { serveiId: number }) {
  const { t } = useTranslation('administracio');
  const tc = useTranslation('comu').t;
  const missatgeError = useMissatgeError();
  const { pot } = useMe();
  const llista = useUsuarisInformar(serveiId);
  const baixa = useBaixaUsuariInformar(serveiId);
  const [cerca, setCerca] = useState('');
  const [form, setForm] = useState<{ persona: UsuariInformarDto | null } | null>(null);
  const [treu, setTreu] = useState<UsuariInformarDto | null>(null);
  const persones = useMemo(() => llista.data ?? [], [llista.data]);
  const q = normalitza(cerca.trim());
  const visibles = q
    ? persones.filter((u) => normalitza(`${u.nom} ${u.email ?? ''} ${u.telefon ?? ''}`).includes(q))
    : persones;
  const potEditar = pot('cai.usuaris-informar', 'MODIFICAR');
  const potEliminar = pot('cai.usuaris-informar', 'ELIMINAR');

  return (
    <div className="panel" data-llista-informar={serveiId}>
      <div className="panel__head est-informar__cap">
        <h3>{t('estructura.informar.titol')}</h3>
        <span className="t-caption-2 dim">
          {t('estructura.informar.resum', { count: persones.length })}
        </span>
        <div className="spacer" />
        <Permis recurs="cai.usuaris-informar" tipus="ALTA">
          <Link className="btn btn--xs" to={`/admin/usuaris-informar/importa?servei=${serveiId}`}>
            <Icona nom="tray" />
            {t('estructura.informar.importa')}
          </Link>
        </Permis>
        <button
          type="button"
          className="btn btn--xs"
          disabled={persones.length === 0}
          onClick={() =>
            exportaCsv(
              `persones-informar-${serveiId}`,
              persones,
              [
                t('estructura.informar.nom'),
                t('estructura.informar.email'),
                t('estructura.informar.telefon'),
                t('estructura.informar.canals'),
                t('estructura.informar.origen'),
              ],
              (c) => tc(`canal.${c}`),
            )
          }
        >
          <Icona nom="doc-export" />
          {t('estructura.informar.exporta')}
        </button>
        <Permis recurs="cai.usuaris-informar" tipus="ALTA">
          <button
            type="button"
            className="btn btn--dark btn--xs"
            onClick={() => setForm({ persona: null })}
          >
            <Icona nom="plus" />
            {t('estructura.informar.afegeix')}
          </button>
        </Permis>
      </div>
      {llista.isError ? (
        <ErrorCarrega onReintenta={() => void llista.refetch()} />
      ) : persones.length === 0 ? (
        <Buit icona="person-3">{llista.isLoading ? '…' : t('estructura.informar.buit')}</Buit>
      ) : (
        <>
          {persones.length > 6 ? (
            <div className="est-informar__cerca">
              <label className="search">
                <Icona nom="magnifier" />
                <input
                  type="search"
                  value={cerca}
                  onChange={(e) => setCerca(e.target.value)}
                  placeholder={t('estructura.informar.cerca')}
                  aria-label={t('estructura.informar.cerca')}
                />
              </label>
            </div>
          ) : null}
          {visibles.length === 0 ? (
            <Buit icona="magnifier">{t('estructura.informar.senseCoincidencies')}</Buit>
          ) : (
            <div className="est-taula-scroll">
              <table className="taula">
                <thead>
                  <tr>
                    <th>{t('estructura.informar.nom')}</th>
                    <th>{t('estructura.informar.contacte')}</th>
                    <th>{t('estructura.informar.canals')}</th>
                    <th>{t('estructura.informar.origen')}</th>
                    <th aria-label={t('estructura.informar.edita')} />
                  </tr>
                </thead>
                <tbody>
                  {visibles.map((u) => (
                    <tr key={u.id ?? u.nom} data-persona={u.id}>
                      <td>
                        <span className="strong">{u.nom}</span>
                        {u.usuariId == null ? (
                          <span
                            className="badge badge--incident est-informar__sense"
                            title={t('estructura.informar.senseCompteAjuda')}
                          >
                            {t('estructura.informar.senseCompte')}
                          </span>
                        ) : null}
                      </td>
                      <td className="dim">
                        <div>{u.email ?? '—'}</div>
                        {u.telefon ? <div className="t-caption">{u.telefon}</div> : null}
                      </td>
                      <td>{u.canals.map((c) => tc(`canal.${c}`)).join(' · ') || '—'}</td>
                      <td>
                        <span className={`badge ${BADGE_ORIGEN[u.origen]}`}>
                          {t(`estructura.informar.origens.${u.origen}`)}
                        </span>
                      </td>
                      <td className="est-informar__accions">
                        {potEditar ? (
                          <button
                            type="button"
                            className="btn btn--xs"
                            aria-label={t('estructura.informar.editaPersona', { nom: u.nom })}
                            onClick={() => setForm({ persona: u })}
                          >
                            {t('estructura.informar.edita')}
                          </button>
                        ) : null}
                        {potEliminar ? (
                          <button
                            type="button"
                            className="btn btn--ghost btn--xs"
                            aria-label={t('estructura.informar.treuPersona', { nom: u.nom })}
                            title={t('estructura.informar.treuPersona', { nom: u.nom })}
                            onClick={() => setTreu(u)}
                          >
                            <Icona nom="trash" className="c-red" />
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {form ? (
        <FormPersona serveiId={serveiId} persona={form.persona} onTanca={() => setForm(null)} />
      ) : null}
      {treu ? (
        <Confirmacio
          titol={t('estructura.informar.treuTitol', { nom: treu.nom })}
          textAccio={t('estructura.informar.treu')}
          perill
          ocupat={baixa.isPending}
          onTanca={() => setTreu(null)}
          onConfirma={() =>
            treu.id != null && baixa.mutate(treu.id, { onSuccess: () => setTreu(null) })
          }
        >
          <p>{t('estructura.informar.treuText')}</p>
          {baixa.isError ? <p className="error-text">{missatgeError(baixa.error)}</p> : null}
        </Confirmacio>
      ) : null}
    </div>
  );
}
